import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { Badge, badgeVariants } from "../../components/ui/badge";
import { getStatusConfig } from "../../utils/statusConfig";
import { Button } from "../../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { CalendarDays, UserRound, Timer, Building2, Eye, Edit2, FileSearch, CheckCircle, ChevronDown, ChevronUp, Clock, Pause, XCircle, FileX, MessageSquare, RefreshCw, Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";

import CommentPopup from "../CommentPopup";
import { CommentIcon } from "../../types/prospect";
import { Comment } from "../../types/deals";
import { RMList } from "../../utils/interfaces";
import { LenderStatusDialog } from "../../components/requirementss/LenderStatusDialog";
import { StatusFormValues } from "../../components/requirementss/LenderStatusDialog";
import { StatusHistoryEntry, Requirement } from "../../types/deals";
import { AIMatchPanel } from "../../pages/manage-login/AIMatchPanel";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";
interface RequirementCardProps {
  req: Requirement;
  onView?: (requirementData: any) => void;
  onUpdate?: (updated: Requirement) => void;
  onEdit?: (req: Requirement) => void;
  showCustomer?: boolean;
  requirementStatuses: any[];
  uniqueRmNames?: RMList[];
  lenderStatusList?: any[];
}

function formatAmount(n: number | undefined) {
  if (typeof n !== "number") return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
}

const requirementStatusConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  "new_application": { icon: FileSearch, color: "text-slate-600", bgColor: "bg-slate-50 dark:bg-slate-950/30", variant: "outline" },
  "due_diligence": { icon: FileSearch, color: "text-blue-600", bgColor: "bg-blue-50 dark:bg-blue-950/30", variant: "secondary" },
  "unresponsive": { icon: Clock, color: "text-gray-600", bgColor: "bg-gray-50 dark:bg-gray-950/30", variant: "outline" },
  "on_hold": { icon: Pause, color: "text-yellow-600", bgColor: "bg-yellow-50 dark:bg-yellow-950/30", variant: "secondary" },
  "cc_approved": { icon: CheckCircle, color: "text-green-600", bgColor: "bg-green-50 dark:bg-green-950/30", variant: "default" },
  "cl_rejected": { icon: XCircle, color: "text-red-600", bgColor: "bg-red-50 dark:bg-red-950/30", variant: "destructive" },
  "cc_rejected": { icon: FileX, color: "text-red-600", bgColor: "bg-red-50 dark:bg-red-950/30", variant: "destructive" },
};

export function RequirementCard({ req, onView, onUpdate, onEdit, showCustomer = false, requirementStatuses,uniqueRmNames,lenderStatusList }: RequirementCardProps) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const navigate = useNavigate();
  const [lendersOpen, setLendersOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [aiMatchOpen, setAiMatchOpen] = useState(false);
  const [statusOpenFor, setStatusOpenFor] = useState<string | null>(null);
  const handleView = () => {
    if (onView) {
      onView(req as any);
    }
  };

  const handleCustomerClick = () => {
    if (!req?.customer?.id) return;
    navigate(`/manage-customer/${req.customer.id}`);
  };

  const [commentPopup, setCommentPopup] = useState<{
    isOpen: boolean;
    id: string;
    type:string;
  }>({
    isOpen: false,
    id: "",
    type:'lender'
  });

  const handleApiCall = async (apiCall: () => Promise<any>, successMessage?: string) => {
    try {
      const response = await apiCall();
      // Check if the response indicates success
      if (response.status === 200 || response.status === "success" || response.status === 201) {
        if (successMessage) {
          toast.success(successMessage); // Optionally show a success message
        }
        return response;
      } else {
        toast("Something went wrong. Please try again.");
        throw new Error("API call failed");
      }
    } catch (error: any) {
      console.error("API Error:", error.message); // Log the error for debugging
      if (error.response && error.response.data && error.response.data.message, error.message) {
        // Display the error message from the backend
        toast.error(error.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
      throw error;
    }
  };

  const handleSaveComment = async (commentText: string, mentions?: string[], icon?: CommentIcon) => {
    const { id, type } = commentPopup;

    try {
      // Convert mention IDs to names for display
      const mentionNames = mentions?.map(mentionId => {
        const admin = uniqueRmNames?.find(user => String(user.id) === String(mentionId));
        return admin?.name || mentionId;
      });

      // Send both IDs and names to the backend
      await handleApiCall(
        () =>
          apiService.post(`/customers/create-comment`, {
            commentText,
            mentions, // Original IDs for backend processing
            mentionNames, // Names for display
            icon,
            id,
            type,
          }),
        "Comment added successfully!"
      );

      // Create a new comment object for the UI
      const newComment: Comment = {
        id: Date.now().toString(),
        text: commentText,
        author:  "You", // Use the actual user name
        date: new Date().toISOString(),
        mentions: mentionNames, // Use names for display
        icon,
        resolutionStatus: 'pending'
      };

   
    } catch (error) {
      console.error("Error saving comment:", error);
    }
  };

  

  const lenderStatus = useMemo(() => {
    if (req?.lenders?.length === 0) return "No Lenders";
    const allStatuses = req?.lenders?.map((l: any) => l.statusHistory[l.statusHistory.length - 1]?.statusId).filter(Boolean);
    if (allStatuses?.some((s: any) => String(s) === "9")) return "Disbursed";
    if (allStatuses?.some((s: any) => String(s) === "8") && !allStatuses?.some((s: any)   => String(s) === "9")) return "Sanctioned";
    if (allStatuses?.some((s: any) => String(s) === "6")) return "Term Sheet Shared";
    if (allStatuses?.every((s: any) => String(s) === "7")) return "Rejected";
    if (allStatuses?.some((s: any) => String(s) === "5")) return "On Hold";
    if (allStatuses?.some((s: any) => String(s) === "4")) return "Unresponsive";
    if (allStatuses?.some((s: any) => String(s) === "3")) return "Under Review";
    if (allStatuses?.some((s: any) => String(s) === "2")) return "Credit Discussion Completed";
    if (allStatuses?.some((s: any) => String(s) === "1")) return "Deal Shared";
    return allStatuses?.[0] || "Deal Shared";
  }, [req?.lenders]);

  const kpis = useMemo(() => {
    let totalSanctioned = 0;
    let totalRevenue = 0;
    let totalDisbursed = 0;
    const totalUtilized = req?.disbursedAmount ?? 0;

    req?.lenders?.forEach((l: any) => {
      const sanc = [...l.statusHistory].reverse().find((e) => typeof e.sanctionAmount === "number")?.sanctionAmount;
      const disbEntry = [...l.statusHistory].reverse().find((e) => typeof e.disbursedAmount === "number");
      const revenue = (disbEntry?.customerPayout ?? 0) + (disbEntry?.lenderPayout ?? 0) - (disbEntry?.partnerPayout ?? 0);
      if (typeof sanc === "number") totalSanctioned += sanc;
      if (disbEntry) {
        totalRevenue += revenue;
        totalDisbursed += disbEntry.disbursedAmount ?? 0;
      }
    });

    // if (req?.type === "Leasing" || req?.type === "Smart EPP") {
    //   totalUtilized = (req?.disbursements || []).reduce((sum: any, d: any) => sum + d.disbursedAmount, 0);
    // }

    return {
      requested: req?.quantum,
      sanctioned: totalSanctioned,
      disbursed: totalDisbursed,
      revenue: totalRevenue,
      utilized: totalDisbursed,
      available: Math.max(0, totalSanctioned - totalUtilized),
    };
  }, [req]);

  const handleAfinMatch = () => {
    setAiMatchOpen(true);
  };

  const handleStatusChange = async (statusId: string | number) => {
    if (!req?.id || isUpdatingStatus) return;
    const updated = { ...(req as any), status: statusId } as Requirement;
    if (onUpdate) {
      onUpdate(updated);
    }
    setIsUpdatingStatus(true);
    try {
      const response = await apiService.post<{ data?: Requirement }>("/login/update-requirements-status", {
        requirementId: req.id,
        updates: updated,
      });
      if (response?.data && onUpdate) {
        onUpdate(response.data);
      }
      toast.success("Requirement status updated");
    } catch (err) {
      toast.error("Failed to update requirement status");
      if (onUpdate) {
        onUpdate({ ...(req as any), status: req.status } as any);
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };


  const getActiveComments = (): Comment[] => {
    const { id, type } = commentPopup;  
      for (const len of req.lenders) {
        if (len.id == id) {
          return len.comments;
      }
      } 
    return [];
  };

  const handleCommentClick = (id: string) => {
    setCommentPopup({
      isOpen: true,
      id,
      type:'lender'
    });
  };

  async function handleStatusUpdate(lenderId: string, vals: StatusFormValues) {
    const now = new Date().toISOString();
    
    const updated = req?.lenders?.map((l: any) => {
      if (l.id !== lenderId) return l;
      const statusId = vals.status != null ? Number(vals.status) : undefined;
      const entry: StatusHistoryEntry = {
        status: lenderStatusList?.find((s: any) => String(s.id) === String(vals.status))?.name || "",
        statusId,
        timestamp: now,
        updatedBy: req?.applicationRM || "",
        note: vals.note,
        reason: vals.reason,
      };
      if (vals.termSheetFile) {
        const file = vals.termSheetFile as File;
        entry.termSheetName = file.name;
        entry.termSheetUrl = URL.createObjectURL(file);
      }
      if (typeof vals.sanctionAmount === "number") {
        entry.sanctionAmount = vals.sanctionAmount;
      }
      const sanctionDate = (vals as any).sanctionDate;
      if (sanctionDate) {
        entry.sanctionDate = sanctionDate instanceof Date
          ? sanctionDate.toISOString()
          : sanctionDate;
      }
      const sanctionValidTill = (vals as any).sanctionValidTill;
      if (sanctionValidTill) {
        entry.sanctionValidTill = sanctionValidTill instanceof Date
          ? sanctionValidTill.toISOString()
          : sanctionValidTill;
      }
      if (vals.sanctionLetterFile) {
        const file = vals.sanctionLetterFile as File;
        entry.sanctionLetterName = file.name;
        entry.sanctionLetterUrl = URL.createObjectURL(file);
      }
      if (typeof vals.disbursedAmount === "number") {
        entry.disbursedAmount = vals.disbursedAmount;
        entry.payoutBasis = vals.payoutBasis;
        
        if (vals.payoutBasis === 'manual') {
          entry.customerPayout = vals.customerPayout;
          entry.lenderPayout = vals.lenderPayout;
          entry.partnerPayout = vals.partnerPayout;
        } else {
          const baseAmount = vals.payoutBasis === "sanctioned" 
            ? (entry.sanctionAmount ?? 0)
            : vals.disbursedAmount;
          
          entry.customerPayoutPercent = vals.customerPayoutPercent;
          entry.lenderPayoutPercent = vals.lenderPayoutPercent;
          entry.partnerPayoutPercent = vals.partnerPayoutPercent;
          
          entry.customerPayout = ((vals.customerPayoutPercent ?? 0) / 100) * baseAmount;
          entry.lenderPayout = ((vals.lenderPayoutPercent ?? 0) / 100) * baseAmount;
          entry.partnerPayout = ((vals.partnerPayoutPercent ?? 0) / 100) * baseAmount;
        }
        
        const rev = (entry.customerPayout ?? 0) + (entry.lenderPayout ?? 0) - (entry.partnerPayout ?? 0);
        entry.totalRevenue = rev;
      }
      return { ...l, statusHistory: [...l.statusHistory, entry] };
    });

    await apiService.post('/login/lender-status-update', { lenderId, vals });

    await refreshWorkflow();

    const nextRequirement = { ...req, lenders: updated } as Requirement;
    onUpdate?.(nextRequirement);
    setStatusOpenFor(null);
  }



  const handleResolveComment = async (commentId: string) => {
    // const { id, type } = commentPopup;

    try {
     

      // return response; // Return the response so the CommentPopup knows the API call was successful
    } catch (error) {
      console.error("Error resolving comment:", error);
      throw error; // Rethrow the error so the CommentPopup knows the API call failed
    }
  };

  const reqStatus = requirementStatuses.find((s: any) => String(s.id) === String(req.status))?.slug ;
  const reqStatusConfig = reqStatus ? requirementStatusConfig[reqStatus] : undefined;
  const ReqStatusIcon = reqStatusConfig?.icon || FileSearch;

  // Build KPI items based on type
  const kpiItems = req?.type === "Hybrid"
    ? [
        { label: "Requested", value: `₹${formatAmount(kpis.requested)}`, color: "" },
        { label: "Sanctioned", value: `₹${formatAmount(kpis.sanctioned)}`, color: "text-blue-600" },
        { label: "Disbursed", value: `₹${formatAmount(kpis.disbursed)}`, color: "text-green-600" },
        { label: "Revenue", value: `₹${formatAmount(kpis.revenue)}`, color: "text-amber-600" },
      ]
    : [
        { label: "Requested", value: `₹${formatAmount(kpis.requested)}`, color: "" },
        { label: "Sanctioned", value: `₹${formatAmount(kpis.sanctioned)}`, color: "text-blue-600" },
        { label: "Utilised", value: `₹${formatAmount(kpis.utilized)}`, color: "text-amber-600" },
        { label: "Available", value: `₹${formatAmount(kpis.available)}`, color: "text-green-600" },
      ];

  return (
    <>
    <Card className={`w-full relative overflow-hidden group ${reqStatus === "cc_approved" ? "border-green-500/50" : ""}`}>
      <span 
        className={`absolute left-0 top-0 h-full w-1 transition-all duration-300 group-hover:w-1.5 ${
          reqStatus === "cc_approved" ? "bg-green-500" : "bg-primary"
        }`} 
        aria-hidden 
      />
      <CardHeader className="pb-3 pt-4 px-5">
        <CardTitle className="flex items-center justify-between flex-wrap gap-2 text-sm">
          <div className="flex items-center gap-1.5 flex-wrap">
            {showCustomer && req?.customer && (
              <>
                {req?.customer?.logo ? (
                  <img
                    src={req.customer.logo}
                    alt={req.customer.name}
                    className="h-6 w-6 rounded-full object-cover border cursor-pointer"
                    onClick={handleCustomerClick}
                  />
                ) : (
                  <div
                    className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium cursor-pointer"
                    onClick={handleCustomerClick}
                  >
                    {req?.customer?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="font-medium text-primary text-sm cursor-pointer" onClick={handleCustomerClick}>
                  {req.customer.name}
                </span>
                <span className="text-muted-foreground">•</span>
              </>
            )}
            <Badge variant="secondary" className="h-5">{req?.type}</Badge>
            
              <Badge variant="outline" className="h-5">{req?.productTypeName || req?.type}</Badge>
         
            <span className="font-medium text-sm">#{req?.requirementId}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={isUpdatingStatus || (req?.lenders?.length ?? 0) > 0}
                  className={cn(
                    badgeVariants({ variant: reqStatusConfig?.variant || "outline" }),
                    (req?.lenders?.length ?? 0) > 0
                      ? "cursor-not-allowed opacity-70 pointer-events-none flex items-center gap-1 h-5"
                      : "cursor-pointer flex items-center gap-1 h-5",
                    reqStatus === "cc_approved" ? "bg-green-600 hover:bg-green-700 text-white border-transparent" : "",
                    isUpdatingStatus && "opacity-70 pointer-events-none"
                  )}
                >
                  <ReqStatusIcon className={cn("h-3 w-3", reqStatus === "cc_approved" ? "text-white" : reqStatusConfig?.color || "")} />
                  {requirementStatuses.find((s: any) => String(s.id) === String(req.status))?.name ?? "-"}
                  <ChevronDown className="h-3 w-3 ml-0.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {requirementStatuses.map((status: any) => {
                  const cfg =
                    requirementStatusConfig[String(status?.slug ?? "")] ??
                    requirementStatusConfig["new_application"];
                  const Icon = cfg.icon;
                  return (
                    <DropdownMenuItem
                      key={String(status?.id ?? status?.slug ?? status?.name ?? "")}
                      onClick={() => handleStatusChange(status?.id)}
                      className={`flex items-center gap-2 ${cfg.bgColor}`}
                    >
                      <Icon className={`h-4 w-4 ${cfg.color}`} />
                      <span>{status.name}</span>
                      {String(req?.status) === String(status?.id) && (
                        <CheckCircle className="h-3 w-3 ml-auto text-green-600" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {req?.lenders?.length > 0 && (() => {
              const config = getStatusConfig(lenderStatus as any);
              const StatusIcon = config?.icon;
              return (
                <Badge variant={config?.variant || "outline"} className="h-5">
                  <div className="flex items-center gap-1">
                    {StatusIcon && <StatusIcon className="h-3 w-3" />}
                    {lenderStatus}
                  </div>
                </Badge>
              );
            })()}
            
            {onEdit && (
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onEdit(req)}>
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              onClick={handleAfinMatch}
              className={cn(
                "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500",
                "text-white shadow-md hover:shadow-[0_0_20px_rgba(139,92,246,0.35)]",
                "transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]",
                "gap-1.5 h-6 px-2.5 text-xs rounded-md",
              )}
            >
              <Sparkles className="h-3.5 w-3.5" />
              AFIN Match
            </Button>
            <Button size="sm" variant="secondary" className="h-6 px-2" onClick={handleView}>
              <Eye className="h-3.5 w-3.5 mr-1" /> View
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4 pt-0 space-y-3">
        {/* Merged KPI + Meta row */}
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3 p-3 rounded-md bg-muted/30">
          {kpiItems.map((kpi) => (
            <div key={kpi.label} className="text-center">
              <div className="text-muted-foreground leading-tight">{kpi.label}</div>
              <div className={cn("font-semibold", kpi.color)}>{kpi.value}</div>
            </div>
          ))}
          <div className="text-center">
            <div className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5"><Timer className="h-3 w-3" /> Tenure</div>
            <div className="font-semibold">{req?.tenure} mo</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5"><UserRound className="h-3 w-3" /> RM</div>
            <div className="font-semibold truncate">{req?.applicationRM}</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5"><CalendarDays className="h-3 w-3" /> Date</div>
            <div className="font-semibold">{new Date(req?.createdAt).toLocaleDateString("en-GB")}</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5"><Building2 className="h-3 w-3" /> Lenders</div>
            <div className="font-semibold">{req?.lenders?.length}</div>
          </div>
        </div>

        {/* Tagged Lenders Table */}
        {req?.lenders?.length > 0 && (
          <Collapsible open={lendersOpen} onOpenChange={setLendersOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center justify-between w-full text-muted-foreground hover:text-foreground transition-colors py-1.5">
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  <span className="font-medium">Tagged Lenders ({req?.lenders?.length})</span>
                </div>
                {lendersOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border rounded-md overflow-hidden mt-1.5">
                {/* Table Header */}
                <div className="grid grid-cols-[1.5fr_1fr_1.3fr_1fr_1fr_1fr_auto] gap-0 bg-muted/50 border-b text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  <div className="px-3 py-2">Lender</div>
                  <div className="px-3 py-2">RM</div>
                  <div className="px-3 py-2">Status</div>
                  <div className="px-3 py-2 text-center">Limit</div>
                  <div className="px-3 py-2 text-center">Sanctioned</div>
                  <div className="px-3 py-2 text-center">Disbursed</div>
                  <div className="px-3 py-2 text-right">Actions</div>
                </div>
                {/* Table Rows */}

                {req?.lenders?.map((lender: any ) => {
                  const currentStatus = lender?.statusHistory[lender?.statusHistory?.length - 1]?.status || "Deal Shared";
                  const currentStatusId = lender?.statusHistory[lender?.statusHistory?.length - 1]?.statusId;
                  const config = getStatusConfig(currentStatusId as any);
                  const StatusIcon = config?.icon;
                  const sanc = [...lender?.statusHistory].reverse().find((e) => typeof e.sanctionAmount === "number")?.sanctionAmount;
                  const disbEntry = [...lender?.statusHistory].reverse().find((e) => typeof e.disbursedAmount === "number");
                  const disbAmt = disbEntry?.disbursedAmount;
                  return (
                    <div key={lender?.id} className="grid grid-cols-[1.5fr_1fr_1.3fr_1fr_1fr_1fr_auto] gap-0 border-b last:border-b-0 hover:bg-muted/20 transition-colors">
                      <div className="px-3 py-2.5 flex items-center gap-2">
                       
                        <div className="flex items-center gap-3">
                                {lender?.institutionAvatar ? (
                                  <img
                                    src={
                                      lender?.institutionAvatar.startsWith("http")
                                        ? lender?.institutionAvatar
                                        : `${import.meta.env.VITE_API_BASE_URL}/${lender?.institutionAvatar}`
                                    }
                                    alt={lender?.institutionName}
                                    className="w-[80px] h-[40px] rounded-full object-cover"
                                  />
                                ) : (
                                  <Building2 className="h-8 w-8 text-primary" />
                                )}

                                <div className="flex flex-col">
                                  <span className="font-medium">{lender?.institutionName}</span>
                                  <span className="text-sm text-muted-foreground">{lender?.lenderName}</span>
                                </div>
                              </div>
                      </div>
                      <div className="px-3 py-2.5 flex items-center">{lender?.applicationRM || req?.applicationRM}</div>
                      <div className="px-3 py-2.5 flex items-center">
                        <Badge
                          variant={config?.variant || "outline"}
                          className="font-medium h-5 px-2 gap-1 max-w-[200px] truncate"
                          title={currentStatus}
                        >
                          {StatusIcon && <StatusIcon className="h-3 w-3" />}
                          {currentStatus}
                        </Badge>
                      </div>
                      <div className="px-3 py-2.5 flex items-center justify-end font-medium">
                        {lender?.programLimit ? `₹${formatAmount(lender?.programLimit)}` : "-"}
                      </div>
                      <div className="px-3 py-2.5 flex items-center justify-end font-medium">
                        {typeof sanc === "number" ? `₹${formatAmount(sanc)}` : "-"}
                      </div>
                      <div className="px-3 py-2.5 flex items-center justify-end font-medium">
                        {typeof disbAmt === "number" ? `₹${formatAmount(disbAmt)}` : "-"}
                      </div>
                      <div className="px-3 py-2.5 flex items-center justify-end gap-1">
                        {!(
                          (req?.type === "Leasing" && String(currentStatusId) === "8") ||
                          (req?.type !== "Leasing" && String(currentStatusId) === "9") ||
                          (String(currentStatusId) === "9")
                        ) && (
                          <Button size="icon" variant="ghost" className="h-6 w-6" title="Update Status" onClick={() => setStatusOpenFor(lender?.id ?? null)}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                            title={lender.comments[0]?.text}
                                              className="h-6 w-6"
                                              variant="ghost"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCommentClick(String(lender.id));
                                              }}
                                            >
                                              <MessageSquare className="w-6 h-6" />
                                              
                              </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" title="View" onClick={handleView}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {onEdit && (
                          <Button size="icon" variant="ghost" className="h-6 w-6" title="Edit" onClick={() => onEdit(req)}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>

    <CommentPopup
isOpen={commentPopup.isOpen}
comments={getActiveComments()}
onSave={handleSaveComment}
onResolve={handleResolveComment}
onClose={() => setCommentPopup({ ...commentPopup, isOpen: false })}
relationshipManagers={uniqueRmNames}
currentUser={JSON.parse(localStorage.getItem("detail") || '{}')?.name || "You"}
/>

{statusOpenFor && (() => {
        const lender = req.lenders.find((l) => l.id === statusOpenFor);
        if (!lender) return null;
        const lastSanction = [...lender.statusHistory].reverse().find(e => typeof e.sanctionAmount === 'number')?.sanctionAmount;
        const currentStatus = lender.statusHistory[lender.statusHistory.length - 1]?.statusId;
        return (
          <LenderStatusDialog
            open={!!statusOpenFor}
            onOpenChange={(v) => !v && setStatusOpenFor(null)}
            onSubmit={(vals) => handleStatusUpdate(statusOpenFor, vals)}
            isHybrid={req.type === "Hybrid"}
            sanctionedAmountFromHistory={lastSanction}
            lenderInstitution={lender.institutionName}
            lenderName={lender.lenderName}
            currentStatus={currentStatus as any}
            LenderStatus={lenderStatusList || []}
            isLeasing={req.type === "Leasing" ? true : false}
          />

          
        );
      })()}

    {aiMatchOpen && (
      <AIMatchPanel
        open={aiMatchOpen}
        onOpenChange={setAiMatchOpen}
        requirement={req}
      />
    )}
    </>
  );
}
